import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  TrendingUp, TrendingDown, FileText, Users, PoundSterling,
  CheckCircle2, Clock, ArrowRight,
  Target, AlertTriangle,
  WifiOff, Calendar
} from 'lucide-react';
import { MainLayout } from '../components/MainLayout';
import { AnimatedCounter, PulseDot } from '../components/ui/animated';
import { formatCurrencyCompact } from '../utils/formatCurrency';
import { CalendarSubscriptionDialog } from '../components/CalendarExport';

const PERIOD_OPTIONS = [
  { id: 'week', label: 'This Week' },
  { id: 'month', label: 'This Month' },
  { id: 'quarter', label: 'This Quarter' },
  { id: 'year', label: 'This Year' },
];

const MetricCard = ({ title, value, change, changeType, icon: Icon, color, subtitle, animated = true }) => (
  <div className="bg-white rounded-2xl border border-slate-100 p-6 hover-lift kpi-card group">
    <div className="flex items-start justify-between">
      <div className="relative z-10">
        <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
        <p className="font-heading text-3xl font-bold text-[#1e3a5f]">
          {animated && typeof value === 'number' ? (
            <AnimatedCounter value={value} duration={1200} />
          ) : (
            value
          )}
        </p>
        {subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}
        {change && (
          <div className={`flex items-center gap-1 mt-2 text-sm ${
            changeType === 'positive' ? 'text-emerald-600' : 
            changeType === 'negative' ? 'text-red-600' : 'text-slate-500'
          }`}>
            {changeType === 'positive' ? (
              <TrendingUp className="w-4 h-4" />
            ) : changeType === 'negative' ? (
              <TrendingDown className="w-4 h-4" />
            ) : null}
            <span>{change}</span>
          </div>
        )}
      </div>
      <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

export const DashboardFullPage = () => {
  const { user, authFetch } = useAuth();
  const { isConnected, onlineUsers } = useWebSocket(); // lastActivity available but not displayed
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [stats, setStats] = useState(null);
  const [recentEnquiries, setRecentEnquiries] = useState([]);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [teamStats, setTeamStats] = useState([]);
  const [bottlenecks, setBottlenecks] = useState([]);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  const isDirector = user?.role === 'director' || user?.role === 'senior_engineer';

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const loadDashboard = async () => {
    try {
      const [statsRes, enquiriesRes, quotesRes, teamRes, calendarRes] = await Promise.all([
        authFetch(`/dashboard/stats?period=${period}`),
        authFetch('/enquiries?limit=5'),
        authFetch('/quotes?limit=5'),
        isDirector ? authFetch('/dashboard/team-stats') : Promise.resolve({ ok: false }),
        authFetch(`/calendar/events?start=${new Date().toISOString().split('T')[0]}&end=${new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0]}`)
      ]);
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
        
        // Calculate bottlenecks
        const issues = [];
        if (data.pending_approvals > 0) {
          issues.push({
            type: 'approvals',
            count: data.pending_approvals,
            label: 'Pending approvals',
            action: 'Review approvals',
            path: '/approvals',
            urgency: data.pending_approvals > 5 ? 'high' : 'medium'
          });
        }
        if (data.decisions_dnq_yet > 2) {
          issues.push({
            type: 'follow_up',
            count: data.decisions_dnq_yet,
            label: 'Enquiries needing follow-up',
            action: 'Review enquiries',
            path: '/enquiries',
            urgency: 'medium'
          });
        }
        setBottlenecks(issues);
      }
      
      if (enquiriesRes.ok) {
        const data = await enquiriesRes.json();
        setRecentEnquiries(data.enquiries || []);
      }
      
      if (quotesRes.ok) {
        const data = await quotesRes.json();
        setRecentQuotes(data.quotes || []);
      }
      
      if (teamRes.ok) {
        const data = await teamRes.json();
        setTeamStats(data.members || []);
      }
      
      if (calendarRes.ok) {
        const data = await calendarRes.json();
        setUpcomingEvents((data.events || []).slice(0, 8));
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-slate-500">Loading...</div>
        </div>
      </MainLayout>
    );
  }

  // Calculate funnel data
  const funnelData = [
    { stage: 'Enquiries', count: stats?.total_enquiries || 0, color: 'bg-sky-500' },
    { stage: 'Decisions Made', count: (stats?.decisions_quote || 0) + (stats?.decisions_approval || 0) + (stats?.decisions_dnq || 0), color: 'bg-violet-500' },
    { stage: 'Quotes Created', count: stats?.quotes_sent || 0, color: 'bg-emerald-500' },
    { stage: 'Won', count: Math.round((stats?.quotes_sent || 0) * (stats?.win_rate || 0) / 100), color: 'bg-green-600' },
  ];

  const maxFunnel = Math.max(...funnelData.map(f => f.count), 1);

  return (
    <MainLayout>
      <div className="space-y-6 max-w-full overflow-x-hidden" data-testid="dashboard-full-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1e3a5f]">
                Dashboard
              </h1>
              <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100">
                {isConnected ? (
                  <>
                    <PulseDot color="bg-emerald-500" size="w-2 h-2" />
                    <span className="text-xs font-medium text-emerald-700">Live</span>
                  </>
                ) : (
                  <>
                    <WifiOff className="w-3 h-3 text-slate-400" />
                    <span className="text-xs text-slate-500">Offline</span>
                  </>
                )}
                {onlineUsers > 1 && (
                  <span className="text-xs text-slate-500 ml-1">
                    • {onlineUsers} online
                  </span>
                )}
              </div>
            </div>
            <p className="text-slate-500 mt-1">
              {isDirector ? 'Team performance overview' : 'Your performance overview'}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setShowCalendarModal(true)}
              variant="outline"
              size="sm"
              className="rounded-full border-blue-200 text-blue-700 hover:bg-blue-50 hidden sm:flex"
              data-testid="dashboard-calendar-btn"
            >
              <Calendar className="w-4 h-4 mr-1.5" />
              Calendar
            </Button>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-32 sm:w-40" data-testid="period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map(opt => (
                  <SelectItem key={opt.id} value={opt.id}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Bottlenecks Alert */}
        {bottlenecks.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 animate-fade-in-up">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-medium text-amber-800">Action Required</h3>
                <div className="mt-2 space-y-2">
                  {bottlenecks.map((b, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-sm text-amber-700">
                        {b.count} {b.label}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(b.path)}
                        className="text-amber-700 hover:text-amber-800"
                      >
                        {b.action}
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Total Enquiries"
            value={stats?.total_enquiries || 0}
            change={stats?.enquiry_change}
            changeType={stats?.enquiry_change_type}
            icon={FileText}
            color="bg-[#1e3a5f]"
          />
          <MetricCard
            title="Quotes Sent"
            value={stats?.quotes_sent || 0}
            change={stats?.quotes_change}
            changeType={stats?.quotes_change_type}
            icon={PoundSterling}
            color="bg-emerald-500"
          />
          <MetricCard
            title="Win Rate"
            value={`${stats?.win_rate || 0}%`}
            change={stats?.win_rate_change}
            changeType={stats?.win_rate_change_type}
            icon={Target}
            color="bg-sky-500"
          />
          <MetricCard
            title="Pipeline Value"
            value={formatCurrencyCompact(stats?.pipeline_value || 0)}
            change={stats?.pipeline_change}
            changeType={stats?.pipeline_change_type}
            icon={TrendingUp}
            color="bg-violet-500"
          />
        </div>

        {/* Funnel */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <h3 className="font-heading font-semibold text-[#1e3a5f] mb-6">
            Sales Funnel
          </h3>
          <div className="space-y-4">
            {funnelData.map((stage, _idx) => {
              // Calculate percentage based on Enquiries (first stage) as baseline
              const baselineCount = funnelData[0]?.count || 0;
              const percentage = baselineCount > 0 
                ? Math.min(100, Math.round((stage.count / baselineCount) * 100))
                : 0;
              
              return (
                <div key={stage.stage} className="flex items-center gap-4">
                  <div className="w-32 text-sm text-slate-600">{stage.stage}</div>
                  <div className="flex-1 relative h-10">
                    <div 
                      className={`h-full ${stage.color} rounded-lg transition-all duration-500`}
                      style={{ width: `${(stage.count / maxFunnel) * 100}%`, minWidth: stage.count > 0 ? '40px' : '0' }}
                    >
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white font-semibold">
                        {stage.count}
                      </span>
                    </div>
                  </div>
                  <div className="w-16 text-right text-sm text-slate-400">
                    {percentage}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Decision Outcomes */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6 overflow-hidden">
          <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4 md:mb-6">
            Decision Outcomes
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 md:gap-4">
            {[
              { label: 'Quote', value: stats?.decisions_quote || 0, color: 'bg-emerald-500', desc: 'Ready to quote', outcome: 'quote' },
              { label: 'Approval', value: stats?.decisions_approval || 0, color: 'bg-sky-500', desc: 'Needs director', outcome: 'quote_approval_required' },
              { label: 'DNQ', value: stats?.decisions_dnq || 0, color: 'bg-red-500', desc: 'Not suitable', outcome: 'do_not_quote' },
              { label: 'DNQ Yet', value: stats?.decisions_dnq_yet || 0, color: 'bg-amber-500', desc: 'Follow up', outcome: 'do_not_quote_yet' },
              { label: 'Pause', value: stats?.decisions_pause || 0, color: 'bg-slate-500', desc: 'On hold', outcome: 'pause' },
            ].map(item => (
              <div 
                key={item.label} 
                className={`text-center p-2 md:p-4 bg-slate-50 rounded-xl min-w-0 ${item.value > 0 ? 'cursor-pointer hover:bg-slate-100 transition-colors' : ''}`}
                onClick={() => item.value > 0 && navigate(`/enquiries?decision=${item.outcome}`)}
                title={item.value > 0 ? `Click to view ${item.label} enquiries` : ''}
              >
                <div className={`w-10 h-10 md:w-12 md:h-12 ${item.color} rounded-xl mx-auto mb-2 md:mb-3 flex items-center justify-center`}>
                  <span className="text-lg md:text-xl font-bold text-white">{item.value}</span>
                </div>
                <p className="text-xs md:text-sm font-medium text-slate-700 truncate">{item.label}</p>
                <p className="text-xs text-slate-400 mt-0.5 md:mt-1 hidden sm:block">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Enquiries */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-[#1e3a5f]">Recent Enquiries</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/enquiries')}
                className="text-[#1e3a5f]"
              >
                View All
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <div className="divide-y divide-slate-100">
              {recentEnquiries.length > 0 ? (
                recentEnquiries.slice(0, 5).map(enquiry => (
                  <div
                    key={enquiry.id}
                    className="px-6 py-4 hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/enquiry/${enquiry.id}`)}
                    data-testid={`enquiry-${enquiry.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-800">{enquiry.project_name}</p>
                        <p className="text-sm text-slate-500">
                          {enquiry.reference_number} • {enquiry.client_company || enquiry.client_name}
                        </p>
                      </div>
                      <div className={`chip ${
                        enquiry.status === 'new' ? 'bg-sky-500' :
                        enquiry.status === 'decision_made' ? 'bg-violet-500' :
                        enquiry.status === 'quoted' ? 'bg-emerald-500' :
                        'bg-slate-500'
                      } text-white text-xs`}>
                        {enquiry.status?.replace('_', ' ')}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-6 py-8 text-center text-slate-400">
                  No recent enquiries
                </div>
              )}
            </div>
          </div>

          {/* Recent Quotes */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-[#1e3a5f]">Recent Quotes</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/quotes')}
                className="text-[#1e3a5f]"
              >
                View All
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <div className="divide-y divide-slate-100">
              {recentQuotes.length > 0 ? (
                recentQuotes.slice(0, 5).map(quote => (
                  <div
                    key={quote.id}
                    className="px-6 py-4 hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/quotes/${quote.id}`)}
                    data-testid={`quote-${quote.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-800">{quote.quote_number}</p>
                        <p className="text-sm text-slate-500">
                          £{(quote.total || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                      <div className={`chip ${
                        quote.status === 'approved' ? 'bg-emerald-500' :
                        quote.status === 'pending_approval' ? 'bg-amber-500' :
                        quote.status === 'sent' ? 'bg-sky-500' :
                        quote.status === 'accepted' ? 'bg-green-600' :
                        quote.status === 'rejected' ? 'bg-red-500' :
                        'bg-slate-500'
                      } text-white text-xs`}>
                        {quote.status?.replace('_', ' ')}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-6 py-8 text-center text-slate-400">
                  No recent quotes
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Team Performance (Director only) */}
        {isDirector && teamStats.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <h3 className="font-heading font-semibold text-[#1e3a5f] mb-6">
              Team Performance
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Member</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Enquiries</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Decisions</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Quotes</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Tasks Done</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Outreach</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {teamStats.map(member => (
                    <tr key={member.id} className="hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-[#1e3a5f] rounded-full flex items-center justify-center text-white text-sm">
                            {member.first_name?.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">
                              {member.first_name} {member.last_name}
                            </p>
                            <p className="text-xs text-slate-400 capitalize">{member.role}</p>
                          </div>
                        </div>
                      </td>
                      <td className="text-center py-3 px-4 font-medium">{member.enquiries_count || 0}</td>
                      <td className="text-center py-3 px-4 font-medium">{member.decisions_count || 0}</td>
                      <td className="text-center py-3 px-4 font-medium">{member.quotes_count || 0}</td>
                      <td className="text-center py-3 px-4 font-medium">{member.tasks_completed || 0}</td>
                      <td className="text-center py-3 px-4 font-medium">{member.outreach_count || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Activity Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Tasks Completed</p>
                <p className="font-heading text-2xl font-bold text-[#1e3a5f]">
                  {stats?.tasks_completed || 0}
                </p>
              </div>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-emerald-500 transition-all"
                style={{ width: `${stats?.task_completion_rate || 0}%` }}
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {stats?.task_completion_rate || 0}% completion rate
            </p>
          </div>
          
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-sky-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Contacts Added</p>
                <p className="font-heading text-2xl font-bold text-[#1e3a5f]">
                  {stats?.contacts_added || 0}
                </p>
              </div>
            </div>
            <p className="text-sm text-slate-600">
              {stats?.outreach_count || 0} outreach activities
            </p>
          </div>
          
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pending Approvals</p>
                <p className="font-heading text-2xl font-bold text-[#1e3a5f]">
                  {stats?.pending_approvals || 0}
                </p>
              </div>
            </div>
            <p className="text-sm text-slate-600">
              Awaiting director review
            </p>
          </div>
        </div>
      </div>

      {/* Upcoming Schedule Widget */}
      {upcomingEvents.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-[#1e3a5f] flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Upcoming Schedule
            </h3>
            <button
              onClick={() => navigate('/calendar')}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              View Calendar <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-2">
            {upcomingEvents.map((event, idx) => {
              const isToday = event.start?.split('T')[0] === new Date().toISOString().split('T')[0];
              const colors = {
                task: 'border-l-amber-400 bg-amber-50',
                followup: 'border-l-violet-400 bg-violet-50',
                quote_deadline: 'border-l-red-400 bg-red-50',
                enquiry_deadline: 'border-l-pink-400 bg-pink-50',
                outreach: 'border-l-cyan-400 bg-cyan-50',
                daily_summary: 'border-l-blue-400 bg-blue-50',
              };
              const colorClass = colors[event.type] || 'border-l-slate-300 bg-slate-50';
              
              return (
                <div key={idx} className={`p-3 rounded-lg border-l-4 ${colorClass} flex items-center justify-between`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-800 truncate">{event.title}</p>
                    <p className="text-xs text-slate-500">
                      {isToday ? 'Today' : new Date(event.start).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                      {event.due_time && ` at ${event.due_time}`}
                    </p>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white text-slate-500 capitalize flex-shrink-0 ml-2">
                    {event.type?.replace('_', ' ')}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
            <span>Next 7 days</span>
            <span>{upcomingEvents.length} event{upcomingEvents.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
      )}

      {/* Calendar Modal */}
      <CalendarSubscriptionDialog
        open={showCalendarModal}
        onOpenChange={setShowCalendarModal}
      />
    </MainLayout>
  );
};
